import { normalizeTrainerId } from "./trainer-assistant-honorar.js";
import { emptyPostalAddressFields } from "../masterdata/postal-address.js";
import { filterTrainersByPrimaryRole } from "./trainer-planning-roles.js";
import { isFreeTrainingTrainer } from "./free-training-trainer.js";

/** Mindestmonate zwischen Deaktivierung und Anonymisierung. */
export const TRAINER_ANONYMIZATION_MIN_MONTHS = 6;

/**
 * @param {Date} date
 * @param {number} months
 */
export function addCalendarMonths(date, months) {
  const d = new Date(date.getTime());
  d.setMonth(d.getMonth() + months);
  return d;
}

/**
 * @param {Record<string, unknown>|null|undefined} trainer
 */
export function isTrainerInactive(trainer) {
  return Boolean(trainer?.is_inactive);
}

/**
 * @param {Record<string, unknown>|null|undefined} trainer
 */
export function isTrainerAnonymized(trainer) {
  return Boolean(String(trainer?.anonymized_at || "").trim());
}

/**
 * @param {Record<string, unknown>|null|undefined} trainer
 * @param {Date} [now]
 */
export function isTrainerEligibleForAnonymization(trainer, now = new Date()) {
  if (!trainer || !isTrainerInactive(trainer) || isTrainerAnonymized(trainer))
    return false;
  const raw = trainer.inactive_at;
  if (!raw) return false;
  const since = new Date(String(raw));
  if (!Number.isFinite(since.getTime())) return false;
  const earliest = addCalendarMonths(since, TRAINER_ANONYMIZATION_MIN_MONTHS);
  return now.getTime() >= earliest.getTime();
}

/**
 * @param {string} trainerId
 */
export function buildTrainerAnonymizationUpdates(trainerId) {
  const short = normalizeTrainerId(trainerId).slice(0, 8);
  return {
    first_name: "Trainer",
    last_name: short ? `(anonymisiert ${short})` : "(anonymisiert)",
    ...emptyPostalAddressFields(),
    license_type: null,
    notes: null,
    login_email: null,
    login_role: "trainer",
    paypal_handle: null,
    avatar_url: null,
    avatar_color_index: null,
    availability: [],
    absences: [],
    default_assistant_trainer_id: null,
    default_assistant_honorar_eur: null,
    default_coordinator_fee_type: "none",
    default_coordinator_fee_value: null,
    is_assistant_only: false,
    is_inactive: true,
    anonymized_at: new Date().toISOString(),
  };
}

/**
 * @param {Record<string, unknown>|null|undefined} trainer
 * @param {(key: string, params?: object) => string} t
 */
export function resolveTrainerDisplayName(trainer, t) {
  if (!trainer) return t("trainerstunden.planning.fieldDash");
  if (isTrainerAnonymized(trainer)) {
    const short = normalizeTrainerId(trainer.id).slice(0, 8);
    return t("trainerstunden.trainer.anonymizedDisplayName", {
      id: short || "—",
    });
  }
  const name = `${trainer.first_name || ""} ${trainer.last_name || ""}`.trim();
  return (
    name ||
    normalizeTrainerId(trainer.id) ||
    t("trainerstunden.planning.fieldDash")
  );
}

/**
 * @param {Array<Record<string, unknown>>|null|undefined} trainers
 * @param {{ includeTrainerIds?: string[] }} [opts]
 */
export function filterTrainersForActivePlanning(trainers, opts = {}) {
  const include = new Set(
    (opts.includeTrainerIds || [])
      .map((id) => normalizeTrainerId(id))
      .filter(Boolean),
  );
  return (trainers || []).filter((tr) => {
    const id = normalizeTrainerId(tr?.id);
    if (!id) return false;
    if (include.has(id)) return true;
    return !isTrainerInactive(tr);
  });
}

/**
 * @param {Array<Record<string, unknown>>|null|undefined} trainers
 * @param {{ includeTrainerIds?: string[] }} [opts]
 */
export function filterTrainersForPrimaryPlanning(trainers, opts = {}) {
  return filterTrainersForActivePlanning(
    filterTrainersByPrimaryRole(trainers),
    opts,
  );
}

function sortTrainersByDisplayName(trainers) {
  return [...(trainers || [])].sort((a, b) => {
    const na =
      `${a?.last_name || ""} ${a?.first_name || ""}`.trim() ||
      normalizeTrainerId(a?.id);
    const nb =
      `${b?.last_name || ""} ${b?.first_name || ""}`.trim() ||
      normalizeTrainerId(b?.id);
    return na.localeCompare(nb, "de", { sensitivity: "base" });
  });
}

/**
 * Trainer für Planungs-/Agenda-Filter (Dropdown): nur aktive, primär planbare.
 * @param {Array<Record<string, unknown>>|null|undefined} trainers
 * @param {{ includeTrainerIds?: string[] }} [opts]
 */
export function filterTrainersForPlanningSelection(trainers, opts = {}) {
  return sortTrainersByDisplayName(
    filterTrainersForPrimaryPlanning(trainers, opts),
  );
}

/**
 * Kalender-Spalten: aktive primär planbare Trainer plus inaktive mit bestehenden Terminen.
 * @param {Array<Record<string, unknown>>|null|undefined} trainers
 * @param {Array<Record<string, unknown>>|null|undefined} sessions
 * @param {{ includeTrainerIds?: string[] }} [opts]
 */
export function buildPlanningCalendarTrainerResources(
  trainers,
  sessions,
  opts = {},
) {
  const active = filterTrainersForPrimaryPlanning(trainers, opts);
  const activeIds = new Set(
    active.map((tr) => normalizeTrainerId(tr?.id)).filter(Boolean),
  );
  const sessionTrainerIds = new Set();
  for (const s of sessions || []) {
    const tid = normalizeTrainerId(s?.trainer_id);
    if (tid) sessionTrainerIds.add(tid);
  }
  const include = new Set(
    (opts.includeTrainerIds || [])
      .map((id) => normalizeTrainerId(id))
      .filter(Boolean),
  );
  const inactiveWithSessions = filterTrainersByPrimaryRole(
    trainers || [],
  ).filter((tr) => {
    const id = normalizeTrainerId(tr?.id);
    if (!id || activeIds.has(id)) return false;
    if (include.has(id)) return true;
    if (!isTrainerInactive(tr)) return false;
    return sessionTrainerIds.has(id);
  });
  return sortTrainersByDisplayName([...active, ...inactiveWithSessions]);
}

/**
 * @param {Array<Record<string, unknown>>|null|undefined} sessions
 * @param {string} trainerId
 * @param {string} todayIso YYYY-MM-DD
 */
/**
 * @param {Array<Record<string, unknown>>|null|undefined} trainers
 * @returns {string}
 */
export function firstPrimaryPlanningTrainerId(trainers) {
  const list = filterTrainersForPrimaryPlanning(trainers);
  const tr = list.find((row) => !isFreeTrainingTrainer(row));
  return tr ? normalizeTrainerId(tr.id) : "";
}

/**
 * @param {Array<Record<string, unknown>>|null|undefined} sessions
 * @param {string} trainerId
 * @param {string} todayIso YYYY-MM-DD
 */
export function countFutureSessionsForTrainer(sessions, trainerId, todayIso) {
  const tid = normalizeTrainerId(trainerId);
  const today = String(todayIso || "").slice(0, 10);
  let count = 0;
  let nextDate = "";
  for (const s of sessions || []) {
    const date = String(s?.date || "").slice(0, 10);
    if (!date || (today && date < today)) continue;
    const main = normalizeTrainerId(s?.trainer_id);
    const asst = normalizeTrainerId(s?.assistant_trainer_id);
    if (main !== tid && asst !== tid) continue;
    count += 1;
    if (!nextDate || date < nextDate) nextDate = date;
  }
  return { count, nextDate };
}
